import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RecipeBookApi from '../../services/recipeBookApi';
import RecipeBookDish from './RecipeBookDish';
import { AppendixComplimentary, AppendixPreparations, AppendixUsageSummary, CustomPlatesSection } from './RecipeBookAppendices';
import { useRecipeBookStore } from './recipeBookStore';
import RecipePdfViewer from './RecipePdfViewer';
import { exportAllRecipesPdf } from '../../utils/reviewPdfExport';
import './recipeBook.css';

/**
 * StructuredRecipeBook — the Recipe Book, on screen.
 *
 * Replaces "click Recipe Download, get a file" with "click Recipe Download, read the
 * book" — the same shape the EOM reports use: a full-bleed page, a sticky toolbar, and
 * a Download button that still produces the existing file.
 *
 * WHAT IT DOES NOT DO, ON PURPOSE
 *   • it never adds a row — adding lives in Item Mapping and Preparations (§8.2)
 *   • it never edits a name, unit, price, category or the Usage Summary (§8.1)
 *   • it never saves without Confirm — no autosave, no save-on-blur (§3)
 *   • it never clears the user's edits on a failed save (§12.8)
 *
 * The Download button reuses exportAllRecipesPdf unchanged, so the file this screen
 * produces is byte-identical to the one the nav produced before (§15.1, R7).
 */

const uuid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `rb-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function Cover({ book }) {
  const t = book.totals;
  return (
    <section className="rb-cover">
      <p className="rb-cover-biz">{book.business.display_name}</p>
      <h1 className="rb-cover-title">RECIPE BOOK</h1>
      <p className="rb-cover-count">{t.menu_items} recipes</p>
      <p className="rb-cover-date">
        {new Date(book.generated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
      </p>
    </section>
  );
}

/** The summary the owner asked for: the totals, with the recipe count leading. */
function Summary({ book }) {
  const t = book.totals;
  const tiles = [
    { label: 'Total recipes', value: t.menu_items, hint: 'menu items with a recipe' },
    { label: 'Categories', value: t.categories },
    { label: 'Ingredient rows', value: t.ingredient_rows },
    { label: 'Preparations', value: t.preparations },
    { label: 'Complimentary items', value: t.complimentary },
    { label: 'Fully mapped', value: t.mapped_items, hint: `${t.unmapped_items} with no ingredients` },
  ];
  return (
    <section className="rb-summary" data-testid="rb-summary">
      <h2 className="rb-summary-title">Recipe Book Summary</h2>
      <div className="rb-summary-grid">
        {tiles.map((tile) => (
          <div className="rb-tile" key={tile.label}>
            <span className="rb-tile-value">{Number(tile.value || 0).toLocaleString('en-IN')}</span>
            <span className="rb-tile-label">{tile.label}</span>
            {tile.hint && <span className="rb-tile-hint">{tile.hint}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

function Contents({ book }) {
  return (
    <section className="rb-toc">
      <h2 className="rb-toc-title">Contents</h2>
      <div className="rb-toc-cols">
        {book.categories.map((cat) => (
          <div className="rb-toc-group" key={cat.name}>
            <h4>{cat.name}</h4>
            <ul>
              {cat.items.map((item) => (
                <li key={item.menu_item_id}>
                  <a href={`#rb-dish-${item.menu_item_id}`}>{item.name}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {book.custom_plates?.length > 0 && (
          <div className="rb-toc-group">
            <h4><a href="#rb-custom-plates">Custom Plates</a></h4>
            <ul>
              {book.custom_plates.map((entry) => (
                <li key={entry.menu_item_id}>
                  <a href="#rb-custom-plates">{entry.name} — {entry.plates} {Number(entry.plates) === 1 ? 'plate' : 'plates'}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

export default function StructuredRecipeBook({ goTo, onBack }) {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [banner, setBanner] = useState(null);
  // The generated PDF, held for preview. Non-null means the viewer is open; nothing is
  // on disk while it is set.
  const [pdfPreview, setPdfPreview] = useState(null);

  const store = useRecipeBookStore();
  const { stats } = store;
  // Held in a ref so a retry after a network error reuses the SAME batch_id —
  // that is what makes the retry idempotent server-side (§10.8, §12.8).
  const batchIdRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const data = await RecipeBookApi.getBook();
      setBook(data);
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      if (status === 403) setForbidden(true);
      else if (status === 404) setError('This feature is currently unavailable.');
      else setError(err?.message || 'Failed to load the recipe book');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // §16 case 13 — a browser close with pending changes loses them, so say so first.
  useEffect(() => {
    if (!stats.dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [stats.dirty]);

  const showToast = (message, kind = 'info') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Confirm ───────────────────────────────────────────────────────────────
  const openConfirm = async () => {
    if (stats.invalidCount > 0) {
      showToast(`Fix ${stats.invalidCount} invalid quantit${stats.invalidCount === 1 ? 'y' : 'ies'} first`, 'error');
      return;
    }
    if (!stats.dirty) return;
    const payload = store.buildPayload();
    setImpact(await RecipeBookApi.getImpact(payload)); // null is tolerated by the dialog
    setConfirmOpen(true);
  };

  const doSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setBanner(null);
    if (!batchIdRef.current) batchIdRef.current = uuid();

    const payload = store.buildPayload();
    try {
      const result = await RecipeBookApi.saveBatch({ batchId: batchIdRef.current, ...payload });
      const n = (result.applied?.updates || 0) + (result.applied?.deletes || 0);
      store.clearAfterSave();
      batchIdRef.current = null;

      // Say what happened to Item Mapping too. A save that quietly re-scaled three
      // variants and retired an ingredient has done more than "saved" suggests, and
      // the user is entitled to know before they find out from a stock report.
      const mapping = result.item_mapping || {};
      const extras = [];
      if (mapping.variants_resynced?.length) {
        extras.push(`${mapping.variants_resynced.length} variant${mapping.variants_resynced.length === 1 ? '' : 's'} updated`);
      }
      if (mapping.ingredients_deleted?.length) {
        extras.push(`${mapping.ingredients_deleted.length} unused ingredient${mapping.ingredients_deleted.length === 1 ? '' : 's'} removed`);
      }
      if (mapping.ingredients_retired?.length) {
        extras.push(`${mapping.ingredients_retired.length} retired`);
      }
      showToast(
        `${n} change${n === 1 ? '' : 's'} saved${extras.length ? ` · ${extras.join(' · ')}` : ''}`,
        'success'
      );

      // Refresh from the backend: row_versions, totals, Appendix C and any variant the
      // save re-scaled are all now different from what is on screen.
      await load();
    } catch (err) {
      const status = err?.api?.status;
      const data = err?.api?.data;

      if (status === 409) {
        store.applyConflicts(data?.conflicts);
        setBanner({
          kind: 'conflict',
          text: data?.message || 'Some rows changed since you opened the book. Nothing was saved.',
        });
      } else if (status === 422) {
        store.applyServerFailures(data?.failures);
        setBanner({
          kind: 'invalid',
          text: `${(data?.failures || []).length} row(s) were refused. Nothing was saved.`,
        });
      } else if (status === 401) {
        setBanner({ kind: 'auth', text: 'Your session expired. Sign in again — your edits are still here.' });
      } else if (status === 404) {
        setBanner({ kind: 'off', text: 'This feature is currently unavailable. Your edits are still here.' });
      } else {
        // Network or server error: keep the batch id so Retry is safe.
        setBanner({
          kind: 'retry',
          text: data?.message || err?.message || 'The save could not be completed. Nothing was saved.',
          retry: true,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!stats.dirty) return;
    // eslint-disable-next-line no-alert
    if (window.confirm('Discard all pending changes? This cannot be undone.')) {
      store.discardAll();
      batchIdRef.current = null;
      setBanner(null);
    }
  };

  /**
   * The PDF — built, then SHOWN. Nothing is written to disk here.
   *
   * Two rules meet at this button:
   *   • the file is regenerated FROM THE DATABASE, never from what is on screen, so a
   *     downloaded book can only ever contain confirmed data
   *   • pending changes block it entirely — a file showing uncommitted numbers would
   *     be a lie the moment it left the building
   *
   * `output: 'blob'` returns the document instead of saving it; the viewer owns the
   * Download button, so a file appears only when the user asks for one a second time.
   */
  const handleOpenPdf = async () => {
    if (stats.dirty) {
      showToast('Confirm your changes before opening the PDF', 'error');
      return;
    }
    setExporting(true);
    try {
      const businessId = window.localStorage.getItem('businessId');
      const result = await exportAllRecipesPdf({
        businessId, startDate: null, endDate: null, groupMode: 'sub', output: 'blob',
      });
      if (result && result.blob) {
        setPdfPreview(result);
      } else {
        showToast('The recipe book could not be prepared', 'error');
      }
    } catch (err) {
      showToast(err?.message || 'Could not prepare the PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const barState = useMemo(() => {
    if (saving) return 'saving';
    if (stats.conflictCount > 0) return 'conflict';
    if (stats.invalidCount > 0) return 'invalid';
    if (stats.dirty) return 'dirty';
    return 'clean';
  }, [saving, stats]);

  if (loading) {
    return <div className="rb-page"><div className="rb-state">Loading the recipe book…</div></div>;
  }

  if (forbidden) {
    return (
      <div className="rb-page">
        <div className="rb-state">
          <h2>Owner access required</h2>
          <p>The Structured Recipe Book is available to the business owner only.</p>
          {onBack && <button type="button" className="rb-btn" onClick={onBack}>Go back</button>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rb-page">
        <div className="rb-state">
          <h2>Could not open the recipe book</h2>
          <p>{error}</p>
          <button type="button" className="rb-btn" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  if (!book) return null;

  return (
    <div className="rb-page">
      {/* Sticky confirm bar — §12.5. Pinned so it is reachable at every scroll position. */}
      <div className={`rb-bar rb-bar-${barState}`}>
        <div className="rb-bar-left">
          <strong>📖 Structured Recipe Book</strong>
          <span className="rb-bar-sub">
            {book.business.display_name} · {book.totals.menu_items} recipes
          </span>
        </div>
        <div className="rb-bar-mid">
          {barState === 'clean' && <span>No pending changes</span>}
          {barState === 'dirty' && (
            <span>
              ⚠ {stats.editCount + stats.deleteCount} pending ·{' '}
              {stats.editCount} edit{stats.editCount === 1 ? '' : 's'}, {stats.deleteCount} removal
              {stats.deleteCount === 1 ? '' : 's'}
            </span>
          )}
          {barState === 'invalid' && <span>Fix {stats.invalidCount} invalid quantity</span>}
          {barState === 'conflict' && <span>{stats.conflictCount} row(s) changed elsewhere</span>}
          {barState === 'saving' && <span>Saving…</span>}
        </div>
        <div className="rb-bar-right">
          <button type="button" className="rb-btn" onClick={handleDiscard} disabled={!stats.dirty || saving}>
            Discard
          </button>
          <button
            type="button"
            className="rb-btn"
            onClick={handleOpenPdf}
            disabled={stats.dirty || saving || exporting}
            title={stats.dirty
              ? 'Confirm your changes before opening the PDF'
              : 'Build the PDF from the saved recipe and preview it'}
          >
            {exporting ? 'Preparing…' : '📄 View / Download PDF'}
          </button>
          <button
            type="button"
            className="rb-btn rb-btn-primary"
            onClick={openConfirm}
            disabled={!stats.dirty || stats.invalidCount > 0 || stats.conflictCount > 0 || saving}
          >
            ✔ Confirm Changes
          </button>
        </div>
      </div>

      {banner && (
        <div className={`rb-banner rb-banner-${banner.kind}`}>
          <span>{banner.text}</span>
          {banner.retry && (
            <button type="button" className="rb-mini" onClick={doSave}>Retry</button>
          )}
          <button type="button" className="rb-mini" onClick={() => setBanner(null)}>Dismiss</button>
        </div>
      )}

      <main className={`rb-book${saving ? ' rb-book-locked' : ''}`}>
        <Cover book={book} />
        <Summary book={book} />
        <Contents book={book} />

        {book.categories.map((cat) => (
          <section className="rb-category" key={cat.name}>
            <h2 className="rb-category-title">{cat.name}</h2>
            {cat.items.map((dish, i) => (
              <RecipeBookDish key={dish.menu_item_id} dish={dish} index={i + 1} store={store} />
            ))}
          </section>
        ))}

        <AppendixComplimentary entries={book.appendix_complimentary} store={store} />
        <AppendixPreparations entries={book.appendix_preparations} store={store} />
        <AppendixUsageSummary entries={book.appendix_usage_summary} />
        {/* Last: dishes saved under Custom plates, each at its own saved plate count. */}
        <CustomPlatesSection entries={book.custom_plates} />

        <footer className="rb-footer">Generated by Invexis</footer>
      </main>

      {confirmOpen && (
        <ConfirmChangesDialog
          store={store}
          impact={impact}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSave}
        />
      )}

      {/* The last step of the flow: the PDF, built from confirmed data, shown before it
          is ever written to disk. */}
      {pdfPreview && (
        <RecipePdfViewer
          blob={pdfPreview.blob}
          fileName={pdfPreview.fileName}
          pageCount={pdfPreview.pageCount}
          title="Recipe Book"
          subtitle={book.business.display_name}
          onClose={() => setPdfPreview(null)}
        />
      )}

      {toast && <div className={`rb-toast rb-toast-${toast.kind}`}>{toast.message}</div>}
    </div>
  );
}

/**
 * §12.6 — the counts plus the blast radius. Stating the blast radius is mandatory
 * whenever a shared preparation or complimentary item is involved.
 */
function ConfirmChangesDialog({ store, impact, onCancel, onConfirm }) {
  const { stats, pendingDeletes } = store;
  const shared = (impact?.shared || []).filter((s) => s.dish_count > 0);

  return (
    <div className="rb-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm changes">
      <div className="rb-modal">
        <h3>Confirm changes</h3>
        <ul className="rb-modal-list">
          {stats.editCount > 0 && <li>{stats.editCount} quantit{stats.editCount === 1 ? 'y' : 'ies'} updated</li>}
          {stats.deleteCount > 0 && <li>{stats.deleteCount} ingredient{stats.deleteCount === 1 ? '' : 's'} removed</li>}
        </ul>

        {impact ? (
          <>
            <p className="rb-modal-impact">
              This affects {impact.affected_menu_items.length} menu item
              {impact.affected_menu_items.length === 1 ? '' : 's'}
              {impact.indirect_count > 0 && ` (${impact.indirect_count} indirectly)`}:
            </p>
            <p className="rb-modal-items">
              {impact.affected_menu_items.slice(0, 12).map((m) => m.name).join(', ')}
              {impact.affected_menu_items.length > 12 && ` and ${impact.affected_menu_items.length - 12} more`}
            </p>
            {shared.map((s) => (
              <p className="rb-modal-warn" key={`${s.kind}:${s.id}`}>
                ⚠ {s.name} is a shared {s.kind === 'preparation' ? 'preparation' : 'complimentary item'}.
                Changing it updates the cost of {s.dish_count} dish{s.dish_count === 1 ? '' : 'es'}.
              </p>
            ))}
          </>
        ) : (
          <p className="rb-modal-warn">
            The blast radius could not be calculated. The change will still be applied to
            the rows you edited and their dishes recalculated.
          </p>
        )}

        {pendingDeletes.size > 0 && (
          <div className="rb-modal-removals">
            <strong>Removing:</strong>
            <ul>
              {[...pendingDeletes.values()].map((d, i) => (
                <li key={i}>
                  {d.name}{d.warning ? ` — ${d.warning}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rb-modal-actions">
          <button type="button" className="rb-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="rb-btn rb-btn-primary" onClick={onConfirm}>Confirm &amp; Save</button>
        </div>
      </div>
    </div>
  );
}
